import { useState } from "react";
import {
  FileText,
  Download,
  Send,
  Calendar,
  Clock,
  DollarSign,
  Package2,
  Truck,
  Gift,
  TrendingUp,
  Mail,
  Phone,
  AlertCircle,
  CheckCircle,
  Loader,
  Package,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import BaseApi from "../../services/base-api";
import { useAuth } from "../../context/AuthContext";
import { formatDateForReports } from "../../utils/dateFormatter";
import axios from "axios";

const getDateInputValue = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
};

const createDefaultGenerateData = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  return {
    reportType: "",
    startDate: getDateInputValue(thirtyDaysAgo),
    endDate: getDateInputValue(today),
    format: "excel",
  };
};

const extractFilename = (
  contentDisposition: string | undefined,
  reportType: string,
  format: string,
) => {
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (simpleMatch?.[1]) {
      return simpleMatch[1];
    }
  }

  const extension =
    format === "pdf" ? "pdf" : format === "json" ? "json" : "xlsx";
  return `${reportType}-report.${extension}`;
};

const ReportsManagementPage = () => {
  const [loading, setLoading] = useState<{
    report: Record<string, boolean>;
    send: boolean;
    schedule: boolean;
  }>({
    report: {},
    send: false,
    schedule: false,
  });
  const { t } = useTranslation();
  const { success, err } = useToast();
  const [scheduleModal, setScheduleModal] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [generateData, setGenerateData] = useState(createDefaultGenerateData);
  const [scheduleData, setScheduleData] = useState({
    reportType: "",
    frequency: "daily",
    time: "09:00",
    recipients: "",
  });
  const [sendData, setSendData] = useState({
    reportType: "",
    phoneNumber: "",
    method: "whatsapp",
    startDate: formatDateForReports(new Date("2020-01-01")),
    endDate: formatDateForReports(new Date("2050-01-01")),
  });

  const { token } = useAuth();

  const reportTypes = [
    {
      id: "sales",
      title: "Sales Report",
      description: "Comprehensive sales analytics and revenue breakdown",
      icon: DollarSign,
      endpoint: `/reports/sales?startDate=${sendData.startDate}&endDate=${sendData.endDate}`,
      color: "bg-green-100 text-green-800",
      iconBg: "bg-green-500",
    },
    {
      id: "inventory",
      title: "Inventory Report",
      description: "Stock levels, low stock alerts, and inventory value",
      icon: Package2,
      endpoint: "/reports/inventory",
      color: "bg-blue-100 text-blue-800",
      iconBg: "bg-blue-500",
    },
    {
      id: "financial",
      title: "Financial Report",
      description: "Revenue, expenses, profit margins, and financial health",
      icon: TrendingUp,
      endpoint: "/reports/financial",
      color: "bg-purple-100 text-purple-800",
      iconBg: "bg-purple-500",
    },
    {
      id: "delivery",
      title: "Delivery Report",
      description: "Shipping status, delivery times, and logistics analytics",
      icon: Truck,
      endpoint: `/reports/delivery?startDate=${sendData.startDate}&endDate=${sendData.endDate}`,
      color: "bg-orange-100 text-orange-800",
      iconBg: "bg-orange-500",
    },
  ];

  const frequencies = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  const openGenerateModal = (reportType = "") => {
    setGenerateData((prev) => ({
      ...prev,
      reportType,
    }));
    setGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setGenerateModal(false);
    setGenerateData(createDefaultGenerateData());
  };

  async function downloadReportAxios(
    type: string,
    startDate: string,
    endDate: string,
    format = "excel",
  ) {
    if (!token) {
      throw new Error("Authentication token not found");
    }

    setLoading((prev) => ({
      ...prev,
      report: { ...prev.report, [type]: true },
    }));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reports/send`,
        {
          type,
          startDate: formatDateForReports(new Date(`${startDate}T00:00:00`)),
          endDate: formatDateForReports(new Date(`${endDate}T23:59:59`)),
          format,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      const disposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const fileName = extractFilename(disposition, type, format);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading((prev) => ({
        ...prev,
        report: { ...prev.report, [type]: false },
      }));
    }
  }

  const submitGenerate = async () => {
    if (
      !generateData.reportType ||
      !generateData.startDate ||
      !generateData.endDate ||
      !generateData.format
    ) {
      err("Please select the report type, format, start date, and end date.");
      return;
    }

    if (new Date(generateData.startDate) > new Date(generateData.endDate)) {
      err("Start date cannot be later than end date.");
      return;
    }

    try {
      await downloadReportAxios(
        generateData.reportType,
        generateData.startDate,
        generateData.endDate,
        generateData.format,
      );
      success("Report generated successfully.");
      closeGenerateModal();
    } catch (error) {
      console.error("Error generating report:", error);
      err("Failed to generate report. Please try again.");
    }
  };

  // const handleScheduleReport = () => {
  //   setScheduleModal(true);
  // };

  // const handleSendReport = () => {
  //   setSendModal(true);
  // };

  const submitSchedule = async () => {
    try {
      setLoading({ ...loading, schedule: true });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In real implementation:
      // const response = await BaseApi.post('/reports/schedule', scheduleData);

      success("Report scheduled successfully!");
      setScheduleModal(false);
      setScheduleData({
        reportType: "",
        frequency: "daily",
        time: "09:00",
        recipients: "",
      });
    } catch (error) {
      console.error("Error scheduling report:", error);
      err("Failed to schedule report. Please try again.");
    } finally {
      setLoading({ ...loading, schedule: false });
    }
  };

  const submitSend = async () => {
    try {
      setLoading({ ...loading, send: true });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In real implementation:

      const response = await BaseApi.post("/reports/send", sendData);
      // console.info("Response - ", response);
      if (response.success) {
        success(`Report sent to ${sendData.phoneNumber} via WhatsApp!`);
      }
      setSendModal(false);
      setSendData({
        reportType: "",
        phoneNumber: "",
        method: "",
        startDate: formatDateForReports(new Date("2020-01-01")),
        endDate: formatDateForReports(new Date("2050-01-01")),
      });
    } catch (error) {
      console.error("Error sending report:", error);
      err("Failed to send report. Please try again.");
    } finally {
      setLoading({ ...loading, send: false });
    }
  };

  // if (loading) {
  // return <LoadingComponent title={t('Reports')} />;
  // }

  // 3. ADD BEFORE RETURN STATEMENT (replace 'filtered' with your filtered array name)
  const totalPages = Math.ceil(reportTypes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const isGeneratingSelectedReport = generateData.reportType
    ? !!loading.report[generateData.reportType]
    : false;

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                {t("Reports")}
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                {t("report-text")}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleScheduleReport}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm sm:text-base"
            >
              <Calendar size={18} />
              {t("Schedule Automatic Delivery")}
            </button>
            <button
              onClick={handleSendReport}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm sm:text-base"
            >
              <Send size={18} />
              {t("Send via WhatsApp")}
            </button>
          </div> */}
        </div>

        {/* Reports Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          style={{ animation: "fadeIn 0.5s ease-out 0.1s backwards" }}
        >
          {reportTypes.map((report) => {
            const Icon = report.icon;
            const isLoading = loading.report[report.id];
            return (
              <div
                key={report.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6 border border-gray-100"
              >
                {/* Report Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`${report.iconBg} rounded-lg p-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-1">
                      {report.title}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${report.color}`}
                    >
                      {t("Available")}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  {report.description}
                </p>

                {/* Actions */}
                <div className="flex flex-row space-x-2">
                  <button
                    onClick={() => openGenerateModal(report.id)}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader className="w-4 h-2 animate-spin" />
                    ) : (
                      <Download size={16} />
                    )}
                    {isLoading ? t("Generating...") : t("Generate Report")}
                  </button>

                  {/* <button
                    onClick={() => {
                      setSelectedReport(report);
                      setSendData({ ...sendData, reportType: report.id });
                      setSendModal(true);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-semibold text-sm border border-green-500"
                  >
                    <Send size={16} />
                    {isLoading ? (
                      <Loader className="w-4 h-2 animate-spin" />
                    ) : (
                      t("Send Now")
                    )}
                  </button> */}
                </div>
              </div>
            );
          })}
        </div>
        {/* Info Card */}
        <div
          className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-400"
          style={{ animation: "fadeIn 0.5s ease-out 0.2s backwards" }}
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Report Features
              </h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  •{" "}
                  {t(
                    "Generate reports instantly or schedule for automatic delivery",
                  )}
                </li>
                <li>
                  • {t("Send reports directly to WhatsApp for quick sharing")}
                </li>
                <li>• {t("Export in multiple formats (PDF, Excel, CSV)")}</li>
                <li>
                  •{" "}
                  {t("Customize date ranges and filters for detailed analysis")}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Report Modal */}
      {generateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeGenerateModal}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Download size={22} />
                {t("Generate Report")}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Report Type")}
                </label>
                <select
                  value={generateData.reportType}
                  onChange={(e) =>
                    setGenerateData((prev) => ({
                      ...prev,
                      reportType: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  <option value="">{t("Select a report")}</option>
                  {reportTypes.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Report Format")}
                </label>
                <select
                  value={generateData.format}
                  onChange={(e) =>
                    setGenerateData((prev) => ({
                      ...prev,
                      format: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  <option value="excel">{t("Excel")}</option>
                  <option value="pdf">{t("PDF")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Start Date")}
                </label>
                <input
                  type="date"
                  value={generateData.startDate}
                  onChange={(e) =>
                    setGenerateData((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("End Date")}
                </label>
                <input
                  type="date"
                  value={generateData.endDate}
                  min={generateData.startDate}
                  onChange={(e) =>
                    setGenerateData((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    {t(
                      "Choose a report type and date range to generate and download the file.",
                    )}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeGenerateModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={submitGenerate}
                  disabled={
                    !generateData.reportType ||
                    !generateData.format ||
                    !generateData.startDate ||
                    !generateData.endDate ||
                    isGeneratingSelectedReport
                  }
                  className="flex-1 px-4 py-3 bg-[#0d223f] text-white rounded-lg hover:bg-[#09182d] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isGeneratingSelectedReport ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      {t("Generating...")}
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      {t("Generate Report")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setScheduleModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar size={24} />
                {t("Schedule Report Delivery")}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Report Type")}
                </label>
                <select
                  value={scheduleData.reportType}
                  onChange={(e) =>
                    setScheduleData({
                      ...scheduleData,
                      reportType: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  <option value="">{t("Select a report")}</option>
                  {reportTypes.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Frequency")}
                </label>
                <select
                  value={scheduleData.frequency}
                  onChange={(e) =>
                    setScheduleData({
                      ...scheduleData,
                      frequency: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  {frequencies.map((freq) => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Delivery Time")}
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="time"
                    value={scheduleData.time}
                    onChange={(e) =>
                      setScheduleData({ ...scheduleData, time: e.target.value })
                    }
                    className="w-full pl-11 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Email Recipients")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={scheduleData.recipients}
                    onChange={(e) =>
                      setScheduleData({
                        ...scheduleData,
                        recipients: e.target.value,
                      })
                    }
                    placeholder="email1@example.com, email2@example.com"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t("Separate multiple emails with commas")}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setScheduleModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={submitSchedule}
                  disabled={
                    !scheduleData.reportType ||
                    !scheduleData.recipients ||
                    loading.schedule
                  }
                  className="flex-1 px-4 py-3 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading.schedule ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      {t("Scheduling...")}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      {t("Schedule Report")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send WhatsApp Modal */}
      {sendModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setSendModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Send size={24} />
                {t("Send Report via WhatsApp")}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Report Type")}
                </label>
                <select
                  value={sendData.reportType}
                  onChange={(e) =>
                    setSendData({ ...sendData, reportType: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-green-600 focus:outline-none"
                >
                  <option value="">Select a report</option>
                  {reportTypes.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("WhatsApp Number")}
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={sendData.phoneNumber}
                    onChange={(e) =>
                      setSendData({ ...sendData, phoneNumber: e.target.value })
                    }
                    placeholder="+1234567890"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:border-green-600 focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t("Include country code (e.g., +1 for US)")}
                </p>
              </div>

              {/* Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">
                    {t(
                      "The report will be generated and sent immediately to the provided WhatsApp number.",
                    )}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setSendModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={submitSend}
                  disabled={
                    !sendData.reportType ||
                    !sendData.phoneNumber ||
                    loading.send
                  }
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading.send ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      {t("Sending...")}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {t("Send Report")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsManagementPage;
